import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

/**
 * Service for story code related API calls.
 */
export default Service.extend({
  api: service(),
  store: service(),
  shData: service(),

  /**
   * Create a new story code
   *
   * @param {Company} company
   * @param {String} body
   * @return {Ember.RSVP.Promise}
   */
  createStoryCode(company, body) {
    const options = {
      jsonData: {
        'company_id': company.id,
        'body' : body
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return this.api.post(`story-codes/`, options)
      .then((response) => {
        this.shData.pushResponseToStore(response);
        return this.store.peekRecord('storyCode', response.data.id);
      })
      .catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error creating your story code. Please refresh and try again.',
          'createStoryCode -- error creating story code.',
        );

        throw e;
      });
  },

  /**
   * Delete a given story code
   *
   * @param {StoryCode} storyCode
   * @return {Ember.RSVP.Promise}
   */
  deleteStoryCode(storyCode) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return this.api.delete(`story-codes/${storyCode.id}/`, options)
      .catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error deleting your story code. Please refresh and try again.',
          'deleteStoryCode -- error deleting story code. code id: ' + storyCode.id,
        );

        throw e;
      });
  },

  /**
   * Get the story codes for a company.
   *
   * @param {Company} company
   * @param {Array<String>=} opt_includes
   * @param {Object=} opt_filters     Additional filters to add.
   * @return {Ember.RSVP.Promise}
   */
  getStoryCodesForCompany(company, opt_includes = [], opt_filters = {}, page = null, count = null) {
    let options = {
      params : {
        filter : {
          company_id : company.id,
          ...opt_filters,
        }
      }
    };

    if (page) {
      options.params['page'] = page;
    }

    if (count) {
      options.params['count'] = count;
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.request('story-codes/', options).then(response => {
      this.shData.pushResponseToStore(response);

      // paginated response object
      if (page) {
        return {
          response: this.shData.getModelArrayFromResponse(response, 'story-code'),
          pagination: this.shData.getPaginationModelFromResponse(response),
        }
      }

      return this.shData.getModelArrayFromResponse(response, 'story-code');
    });
  },

  /**
   * Get a story code by id
   *
   * @param {StoryCode} storyCode
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  getStoryCodeById(codeId, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.get(`story-codes/${codeId}`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('storyCode', response.data.id);
    });
  },

  /**
   * Update a story code
   *
   * @param {StoryCode} storyCode
   * @param {Boolean|null} opt_deactivated
   * @param {Boolean|null} opt_adminOnly
   * @return {Ember.RSVP.Promise}
   */
  updateStoryCode(storyCode, opt_deactivated = null, opt_adminOnly = null) {
    let options = {
      jsonData: {}
    };

    if (opt_deactivated != null) options.jsonData['deactivated'] = opt_deactivated;
    if (opt_adminOnly != null) options.jsonData['admin_only'] = opt_adminOnly;

    this.shData.addIncludesToOptions(options, []);

    return this.api.put(`story-codes/${storyCode.id}`, options);
  },
});
